import { useMsal } from "@azure/msal-react";
import {
  DefaultButton,
  getTheme,
  mergeStyleSets,
  Stack,
} from "@fluentui/react";
import { useContext, useMemo } from "react";
import { DataAccessContext } from "../Contexts/DataAccessContext";
import { useBoolean } from "@fluentui/react-hooks";
import { ExtensionFolder } from "../Types";
import ExtensionsComboBox from "../ExperimentsTable/ExtensionsCombox";
import SettingsPanel from "./SettingsPanel/SettingsPanel";
import { useAppContext } from "../Contexts";
import PreserveParamsLink from "../Shared/LinkComponents/PreserveParamsLink";
import { AppName } from "../Shared/shared.consts";

interface TopBarProps {
  /**
   * List of available extensions, used for the extension dropdown.
   */
  extensions: ExtensionFolder[];
}

/**
 * The top navigation bar component displaying the app name, extension dropdown, and user settings.
 */
const TopBar: React.FC<TopBarProps> = ({ extensions }) => {
  const { accounts } = useMsal();
  const { extensionName } = useAppContext();
  const { activeAccount } = useContext(DataAccessContext);
  const [showSettings, { setTrue: openSettings, setFalse: closeSettings }] =
    useBoolean(false);

  const username = useMemo(() => {
    if (!accounts.length) {
      return "Logging in";
    }

    let text = activeAccount?.username;
    if (accounts.length > 1) {
      text += ` (+${accounts.length - 1})`;
    }

    return text;
  }, [accounts, activeAccount]);

  return (
    <>
      <Stack
        horizontal
        className={styles.topBar}
        horizontalAlign="space-between"
        verticalAlign="center"
      >
        <Stack horizontal verticalAlign="center" horizontalAlign="center">
          <Stack.Item>
            <h1 className={styles.home}>
              <PreserveParamsLink to="/">{AppName}</PreserveParamsLink>
            </h1>
          </Stack.Item>
          <Stack.Item grow={1}>
            <ExtensionsComboBox
              extensions={extensions}
              selected={extensionName}
            />
          </Stack.Item>
        </Stack>
        <Stack>
          <Stack.Item>
            <DefaultButton
              iconProps={{ iconName: "Settings" }}
              onClick={openSettings}
              className={styles.settings}
            >
              <Stack horizontalAlign="start">
                <Stack.Item>
                  <span>{username}</span>
                </Stack.Item>
              </Stack>
            </DefaultButton>
          </Stack.Item>
        </Stack>
      </Stack>
      {showSettings ? <SettingsPanel dismissPanel={closeSettings} /> : null}
    </>
  );
};
export default TopBar;

const theme = getTheme();

const styles = mergeStyleSets({
  topBar: {
    backgroundColor: theme.palette.themePrimary,
    color: theme.palette.white,
    fontSize: 15,
    height: 50,
  },
  home: {
    fontSize: 15,
    padding: "0 15px",
    "> a": {
      color: theme.palette.white,
      textDecoration: "none",
    },
    "> a:focus": {
      textDecoration: "none",
      color: theme.palette.white,
    },
    "> a:hover": {
      textDecoration: "none",
      color: theme.palette.themeLight,
    },
  },
  settings: {
    backgroundColor: theme.palette.themePrimary,
    color: theme.palette.white,
    border: "none",
  },
});
